import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Check, CreditCard, Handshake, ShieldCheck, User, X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AGREEMENT_KEYS, AGREEMENT_VERSION } from "@/lib/partnerConstants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESUME_KEY = "miro_one_partner_draft";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

// Razorpay Payment Page for Professional Partner membership — used while
// live Standard Checkout is under Razorpay account review.
const RAZORPAY_PAYMENT_PAGE = "https://rzp.io/rzp/0ltjHzYg";

// Client-side fallback if the server fee preview hasn't arrived yet.
const CLIENT_FEE = {
  professional: {
    partner_type: "professional",
    membership_fee: 2500, membership_gst: 450, gst_rate: 0.18,
    total: 2950, currency: "INR",
  },
  franchise: {
    partner_type: "franchise",
    franchise_fee: 75000, franchise_gst: 13500, membership_fee: 2500,
    gst_rate: 0.18, total: 91000, currency: "INR",
  },
};

export default function PartnerRegister() {
  // Draft / resume state
  const [appId, setAppId] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Flow state: null (filling KYC) → "awaiting_payment" → "paid"
  const [phase, setPhase] = useState("kyc");
  const [submitted, setSubmitted] = useState(null); // {payment_reference, fee}
  const [paid, setPaid] = useState(null);           // verify-payment response
  const [payingOnline, setPayingOnline] = useState(false);

  const [fees, setFees] = useState(CLIENT_FEE);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    partner_type: "professional",
    first_name: "", last_name: "", dob: "",
    mobile: "", email: "",
    state: "", city: "", pincode: "",
  });

  // ---------- Init: resume draft or start fresh + fee previews -----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Live fee previews for both tracks (Rate Cards → Onboarding fees)
      axios.all([
        axios.get(`${API}/public/partner-fee-preview`, { params: { partner_type: "professional" } }),
        axios.get(`${API}/public/partner-fee-preview`, { params: { partner_type: "franchise" } }),
      ]).then(([p, f]) => {
        if (!cancelled) setFees({ professional: p.data, franchise: f.data });
      }).catch(() => {/* keep CLIENT_FEE fallback */});

      const cached = safeParse(localStorage.getItem(RESUME_KEY));
      if (cached?.id && cached?.token) {
        try {
          const r = await axios.get(`${API}/public/partner-applications/${cached.id}`,
                                    { params: { resume_token: cached.token } });
          if (!cancelled) {
            const row = r.data;
            setAppId(cached.id);
            setToken(cached.token);
            setForm((f) => ({
              ...f,
              partner_type: row.partner_type || "professional",
              first_name: row.first_name || "",
              last_name: row.last_name || "",
              dob: row.dob || "",
              mobile: row.mobile || "",
              email: row.email || "",
              state: row.state || "",
              city: row.city || "",
              pincode: row.pincode || "",
            }));
            if (row.status === "payment_pending" && row.payment_reference) {
              setSubmitted({ payment_reference: row.payment_reference,
                             fee: row.fee_snapshot });
              setPhase("awaiting_payment");
            }
            setLoading(false);
            return;
          }
        } catch {
          localStorage.removeItem(RESUME_KEY);
        }
      }
      try {
        const r = await axios.post(`${API}/public/partner-applications/draft`,
                                   { partner_type: "professional" });
        if (cancelled) return;
        setAppId(r.data.id);
        setToken(r.data.resume_token);
        localStorage.setItem(RESUME_KEY, JSON.stringify({
          id: r.data.id, token: r.data.resume_token,
        }));
      } catch {
        toast.error("Unable to start a new application right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const savePatch = useCallback(async (delta) => {
    if (!appId || !token) return;
    await axios.patch(`${API}/public/partner-applications/${appId}`,
                      delta, { params: { resume_token: token } });
  }, [appId, token]);

  const upd = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const kycPayload = useCallback((f) => {
    const delta = {
      partner_type: f.partner_type,
      primary_category: f.partner_type === "franchise"
        ? "franchise_partner" : "other_professional",
    };
    if (f.first_name.trim()) delta.first_name = f.first_name.trim();
    if (f.last_name.trim()) delta.last_name = f.last_name.trim();
    if (f.dob) delta.dob = f.dob;
    if (f.mobile.trim()) delta.mobile = f.mobile.trim();
    if (f.email.trim()) delta.email = f.email.trim();
    if (f.state.trim()) delta.state = f.state.trim();
    if (f.city.trim()) delta.city = f.city.trim();
    if (f.pincode.trim()) delta.pincode = f.pincode.trim();
    return delta;
  }, []);

  // Debounced auto-save so a partial KYC fill survives a page reload.
  const hydratedRef = React.useRef(false);
  useEffect(() => {
    if (!appId || !token || phase !== "kyc") return;
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    const t = setTimeout(() => {
      savePatch(kycPayload(form)).catch(() => {/* re-saved on submit */});
    }, 800);
    return () => clearTimeout(t);
  }, [form, appId, token, phase, savePatch, kycPayload]);

  const setType = async (partner_type) => {
    setForm((f) => ({ ...f, partner_type }));
    try {
      await savePatch({
        partner_type,
        primary_category: partner_type === "franchise"
          ? "franchise_partner" : "other_professional",
      });
    } catch { /* non-blocking; re-saved on submit */ }
  };

  // ---------- Validation ---------------------------------------------------
  const FIELD_TESTIDS = {
    first_name: "partner-first-name", mobile: "partner-mobile",
    email: "partner-email", state: "partner-state",
    city: "partner-city", pincode: "partner-pincode",
    terms: "partner-agree-terms",
  };
  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Please enter your first name";
    if (!/^\d{10}$/.test(form.mobile.trim()))
      errs.mobile = "Enter a valid 10-digit mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Enter a valid email address";
    if (!form.state.trim()) errs.state = "Please enter your state";
    if (!form.city.trim()) errs.city = "Please enter your town / city";
    if (!/^\d{6}$/.test(form.pincode.trim()))
      errs.pincode = "Enter a valid 6-digit PIN code";
    if (!agreed) errs.terms = "Please accept the Terms & Privacy Policy to continue";
    return errs;
  };

  const proceedToPayment = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[data-testid='${FIELD_TESTIDS[firstKey]}']`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus?.();
      toast.error("Please fix the highlighted fields");
      return;
    }
    if (!appId || !token) {
      toast.error("Application session not ready — please reload the page.");
      return;
    }
    setSaving(true);
    try {
      await savePatch(kycPayload(form));
      const r = await axios.post(
        `${API}/public/partner-applications/${appId}/submit`,
        { agreements: AGREEMENT_KEYS.map((k) => ({
            key: k.key, version: AGREEMENT_VERSION, accepted: true })) },
        { params: { resume_token: token } }
      );
      setSubmitted(r.data);
      setPhase("awaiting_payment");
      // Keep RESUME_KEY so a returning visitor lands back on the
      // payment-pending screen; it is cleared only on "Start a new
      // application" or after a verified payment.
      localStorage.setItem(`miro_one_partner_submitted_${appId}`,
        JSON.stringify({ id: appId, token }));
      // Professional partners pay via the Razorpay payment page (works while
      // the account's live Standard Checkout is under Razorpay review).
      // Franchise keeps the integrated checkout popup.
      if (form.partner_type === "franchise") {
        await openRazorpay();
      } else {
        toast.success("Application submitted — complete your payment to activate.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to submit — please try again");
    } finally {
      setSaving(false);
    }
  };

  const openRazorpay = async () => {
    setPayingOnline(true);
    try {
      // Wait up to ~4s for checkout.razorpay.com/v1/checkout.js to load.
      for (let i = 0; i < 20 && !window.Razorpay; i++) {
        await new Promise((r) => setTimeout(r, 200));
      }
      if (!window.Razorpay) {
        setPayingOnline(false);
        toast.error("Payment gateway didn't load", {
          description: "Please check your internet connection or disable ad-blockers, then retry.",
        });
        return;
      }
      const r = await axios.post(
        `${API}/public/partner-applications/${appId}/razorpay/create-order`,
        null, { params: { resume_token: token } });
      const { order_id, amount, currency, key_id, reference,
              applicant_name, applicant_email, applicant_phone } = r.data;
      const options = {
        key: key_id,
        amount,
        currency,
        order_id,
        name: "MIRO Consultancy",
        description: `Partner onboarding — ${reference}`,
        image: "/miro-logo.png",
        theme: { color: "#041B46" },
        prefill: {
          name: applicant_name || "",
          email: applicant_email || "",
          contact: applicant_phone || "",
        },
        modal: { ondismiss: () => {
          setPayingOnline(false);
          toast.message("Checkout cancelled — you can retry below whenever ready.");
        } },
        handler: async (resp) => {
          try {
            const v = await axios.post(
              `${API}/public/partner-applications/${appId}/razorpay/verify-payment`,
              { razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature },
              { params: { resume_token: token } });
            setPaid(v.data);
            setPhase("paid");
            setPayingOnline(false);
            localStorage.removeItem(RESUME_KEY);
          } catch (err2) {
            setPayingOnline(false);
            toast.error(err2?.response?.data?.detail || "Signature verification failed");
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (e) => {
        setPayingOnline(false);
        toast.error(e?.error?.description || "Payment failed", {
          description: e?.error?.reason || "Please retry below",
        });
      });
      rzp.open();
    } catch (e) {
      setPayingOnline(false);
      toast.error(e?.response?.data?.detail || "Unable to start online payment");
    }
  };

  const startAgain = () => {
    localStorage.removeItem(RESUME_KEY);
    window.location.reload();
  };

  const fee = fees[form.partner_type] || CLIENT_FEE.professional;

  // ---------- Screens -------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-[var(--miro-text-muted)]">Preparing your application…</div>
      </div>
    );
  }

  if (phase === "paid" && paid) {
    return <PaidScreen data={paid}/>;
  }

  if (phase === "awaiting_payment" && submitted) {
    return (
      <PaymentPendingScreen
        data={submitted}
        fee={submitted.fee || fee}
        appId={appId}
        paying={payingOnline}
        onPay={openRazorpay}
        onStartAgain={startAgain}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--miro-bg, #F5F7FB)" }}>
      <div className="max-w-3xl mx-auto p-4 sm:p-8" data-testid="partner-register-wizard">
        <Link to="/login" data-testid="partner-register-back"
              className="inline-flex items-center gap-1 text-xs text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]">
          <ArrowLeft size={13}/> Back to sign in
        </Link>

        <div className="mt-4 mb-6">
          <div className="miro-overline">Become a MIRO One Partner</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-[var(--miro-navy)] tracking-tight">
            Membership application
          </h1>
          <p className="text-sm text-[var(--miro-text-secondary)] mt-2 max-w-xl">
            Choose your membership, fill in your basic KYC details and pay online —
            your account is activated automatically once the payment is verified.
          </p>
        </div>

        {/* ---- 1. Membership type --------------------------------------- */}
        <div className="bg-white border border-[var(--miro-border)] rounded-2xl p-5 sm:p-6 mb-5"
             data-testid="partner-step-type">
          <SectionTitle title="1 · Choose your membership"
            subtitle="Same KYC for both — only the fee differs."/>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <TrackCard
              testid="partner-track-professional"
              selected={form.partner_type === "professional"}
              title="Professional Partner"
              description="For individual professionals, consultants and referral partners."
              feeLines={[
                ["Annual membership (12 months)", inr(fees.professional?.membership_fee)],
                [`GST @ ${Math.round((fees.professional?.gst_rate ?? 0.18) * 100)}%`, inr(fees.professional?.membership_gst)],
                ["Total payable", inr(fees.professional?.total)],
              ]}
              onClick={() => setType("professional")}
            />
            <TrackCard
              testid="partner-track-franchise"
              selected={form.partner_type === "franchise"}
              title="Franchise Partner"
              description="For individuals or organisations seeking MIRO franchise identity and branch-level access."
              feeNote="Franchise commercial terms are shared privately at the payment step."
              onClick={() => setType("franchise")}
            />
          </div>
        </div>

        {/* ---- 2. Basic KYC ---------------------------------------------- */}
        <div className="bg-white border border-[var(--miro-border)] rounded-2xl p-5 sm:p-6 mb-5"
             data-testid="partner-step-kyc">
          <SectionTitle title="2 · Basic KYC"
            subtitle="Just the essentials — you can add documents and professional details later from your partner profile."/>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldInput label="First name *" testid="partner-first-name"
                value={form.first_name} onChange={upd("first_name")} required
                error={errors.first_name}/>
              <FieldInput label="Last name" testid="partner-last-name"
                value={form.last_name} onChange={upd("last_name")}
                error={errors.last_name}/>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldInput label="Mobile number *" testid="partner-mobile"
                value={form.mobile} onChange={upd("mobile")} required
                error={errors.mobile}/>
              <FieldInput label="Email ID *" testid="partner-email" type="email"
                value={form.email} onChange={upd("email")} required
                error={errors.email}/>
            </div>
            <FieldInput label="Date of birth" testid="partner-dob" type="date"
              value={form.dob} onChange={upd("dob")}/>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FieldInput label="State *" testid="partner-state"
                value={form.state} onChange={upd("state")} required
                error={errors.state}/>
              <FieldInput label="Town / City *" testid="partner-city"
                value={form.city} onChange={upd("city")} required
                error={errors.city}/>
              <FieldInput label="PIN code *" testid="partner-pincode"
                value={form.pincode} onChange={upd("pincode")} required
                error={errors.pincode}/>
            </div>
          </div>
        </div>

        {/* ---- 3. Terms + pay -------------------------------------------- */}
        <div className="bg-white border border-[var(--miro-border)] rounded-2xl p-5 sm:p-6"
             data-testid="partner-step-pay">
          <SectionTitle title="3 · Accept terms & pay"
            subtitle="One consent covers all MIRO One partner documents — you can view them before accepting."/>
          <label className="flex items-start gap-3 text-sm cursor-pointer mt-4">
            <Checkbox
              data-testid="partner-agree-terms"
              checked={agreed}
              onCheckedChange={() => {
                setAgreed((a) => !a);
                setErrors((er) => (er.terms ? { ...er, terms: undefined } : er));
              }}
              className="mt-0.5"
            />
            <span className="text-[var(--miro-text-primary)]">
              I agree to the{" "}
              <button type="button" data-testid="partner-view-terms"
                      onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                      className="text-[var(--miro-blue)] underline font-semibold">
                Terms &amp; Privacy Policy
              </button>
              <span className="text-[11px] text-[var(--miro-text-muted)]"> · {AGREEMENT_VERSION}</span>
            </span>
          </label>
          {errors.terms && (
            <p className="text-xs text-red-600 mt-1.5" data-testid="partner-agree-terms-error">
              {errors.terms}
            </p>
          )}

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-[var(--miro-border)] pt-5">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[var(--miro-text-muted)]">
                {form.partner_type === "franchise" ? "Franchise fee" : "Membership total"}
              </div>
              {form.partner_type === "franchise" ? (
                <div className="font-display font-bold text-lg text-[var(--miro-navy)]"
                     data-testid="partner-fee-total">Shown at payment</div>
              ) : (
                <div className="font-display font-bold text-2xl text-[var(--miro-navy)]"
                     data-testid="partner-fee-total">{inr(fee?.total)}</div>
              )}
            </div>
            <Button
              data-testid="partner-pay-btn"
              onClick={proceedToPayment}
              disabled={saving || payingOnline}
              className="h-11 gap-1.5 px-6 font-semibold"
              style={{ background: "var(--miro-navy)" }}>
              <CreditCard size={15}/>
              {saving
                ? "Submitting…"
                : form.partner_type === "franchise"
                  ? "Proceed to payment"
                  : `Pay ${inr(fee?.total)} with Razorpay`}
            </Button>
          </div>
          <div className="mt-3 text-[11px] text-[var(--miro-text-muted)] leading-relaxed flex items-start gap-1.5">
            <ShieldCheck size={13} className="shrink-0 mt-0.5 text-[var(--miro-green,#12B981)]"/>
            Secure payment via Razorpay (UPI, cards, net-banking, wallets). Your account is
            activated automatically once the payment is verified. Never pay into personal accounts.
          </div>
        </div>
      </div>

      {showTerms && <TermsModal onClose={() => setShowTerms(false)}/>}
    </div>
  );
}

// ============================================================================
// Terms modal — the same partner documents as before, viewable before consent
// ============================================================================
function TermsModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
         data-testid="partner-terms-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="miro-overline">MIRO One partner documents</div>
            <h2 className="font-display font-bold text-xl text-[var(--miro-navy)] mt-1">
              Terms, policies &amp; declarations
            </h2>
          </div>
          <button onClick={onClose} data-testid="partner-terms-close"
                  className="text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]">
            <X size={18}/>
          </button>
        </div>
        <p className="text-xs text-[var(--miro-text-secondary)] mt-3 leading-relaxed">
          By ticking the consent box you accept each of the following documents
          (version {AGREEMENT_VERSION}). We record the version, timestamp, IP
          address and device for every acceptance.
        </p>
        <ul className="mt-4 space-y-2">
          {AGREEMENT_KEYS.map((k) => (
            <li key={k.key}
                className="flex items-start gap-2 text-sm text-[var(--miro-text-primary)] border border-[var(--miro-border)] rounded-lg px-3 py-2 bg-[var(--miro-bg)]">
              <Check size={14} className="mt-0.5 shrink-0 text-[var(--miro-green,#12B981)]"/>
              {k.label}
            </li>
          ))}
        </ul>
        <Button onClick={onClose} data-testid="partner-terms-ok"
                className="w-full mt-5 h-10" style={{ background: "var(--miro-navy)" }}>
          Got it
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Post-submit: payment pending (popup dismissed / failed → retry or manual)
// ============================================================================
function PaymentPendingScreen({ data, fee, appId, paying, onPay, onStartAgain }) {
  const isProfessional = (fee?.partner_type || data?.fee?.partner_type) !== "franchise";
  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: "var(--miro-bg, #F5F7FB)" }}>
      <div className="max-w-md w-full bg-white border border-[var(--miro-border)] rounded-2xl p-8"
           data-testid="partner-payment-pending">
        <div className="miro-overline text-center">Application submitted</div>
        <h1 className="font-display font-black text-3xl text-center text-[var(--miro-navy)] mt-1">
          Complete your payment
        </h1>
        <p className="text-sm text-[var(--miro-text-secondary)] mt-4 text-center leading-relaxed">
          {isProfessional
            ? "Pay securely on Razorpay, then return here and submit your payment reference — MIRO verifies it and activates your account."
            : "Your application is saved. Pay online now for instant activation, or use the manual UPI / bank-transfer option."}
        </p>
        <div className="mt-5 rounded-lg border border-[var(--miro-border)] bg-[var(--miro-bg)] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[var(--miro-text-muted)]">
            Your unique payment reference
          </div>
          <div className="font-mono text-lg font-bold text-[var(--miro-navy)] mt-1"
               data-testid="partner-submitted-reference">
            {data.payment_reference}
          </div>
          {isProfessional && (
            <div className="text-[11px] text-[var(--miro-text-muted)] mt-1.5">
              Quote this reference when submitting your payment details after paying.
            </div>
          )}
        </div>
        <div className="mt-5 flex flex-col gap-2">
          {isProfessional ? (
            <>
              <Button asChild data-testid="partner-pay-page-btn"
                      className="h-11 font-semibold gap-1.5" style={{ background: "var(--miro-navy)" }}>
                <a href={RAZORPAY_PAYMENT_PAGE} target="_blank" rel="noopener noreferrer">
                  <CreditCard size={15}/> Pay {inr(fee?.total)} on Razorpay
                </a>
              </Button>
              <Button asChild variant="outline" data-testid="partner-manual-pay-btn"
                      className="h-11 font-semibold">
                <Link to={`/partner/payment/${appId}`}>I've paid — submit payment reference</Link>
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="partner-retry-pay-btn" onClick={onPay} disabled={paying}
                      className="h-11 font-semibold gap-1.5" style={{ background: "var(--miro-navy)" }}>
                <CreditCard size={15}/>
                {paying ? "Opening…" : `Pay ${inr(fee?.total)} online`}
              </Button>
              <Button asChild variant="outline" data-testid="partner-manual-pay-btn"
                      className="h-11 font-semibold">
                <Link to={`/partner/payment/${appId}`}>Pay manually via UPI / bank transfer</Link>
              </Button>
            </>
          )}
          <Button asChild variant="ghost" data-testid="partner-pending-login-btn"
                  className="h-11">
            <Link to="/login">Back to sign in</Link>
          </Button>
          <button data-testid="partner-submitted-restart" onClick={onStartAgain}
                  className="text-[11px] text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]">
            Start a new application
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Payment verified
// ============================================================================
function PaidScreen({ data }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: "var(--miro-bg, #F5F7FB)" }}>
      <div className="max-w-md w-full bg-white border border-[var(--miro-border)] rounded-2xl p-8"
           data-testid="payment-online-success">
        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
             style={{ background: "var(--miro-green, #12B981)" }}>
          <Check className="text-white" size={26}/>
        </div>
        <div className="miro-overline text-center mt-6">Payment verified</div>
        <h1 className="font-display font-black text-3xl text-center text-[var(--miro-navy)] mt-1">
          Welcome to MIRO One
        </h1>
        <p className="text-sm text-[var(--miro-text-secondary)] mt-4 text-center">
          Your payment for <strong>{data.reference}</strong> has been verified automatically.
          {data.auto_approved
            ? " Your account is now active — sign in with the credentials from your welcome email."
            : " Your application is now under review — we'll notify you by email once your account is activated."}
        </p>
        <div className="mt-5 rounded-lg border border-[var(--miro-border)] bg-[var(--miro-bg)] p-4 text-sm text-[var(--miro-text-primary)]">
          <div className="flex justify-between"><span>Payment ID</span><strong className="font-mono text-xs">{data.payment_id}</strong></div>
          <div className="flex justify-between mt-1"><span>Application status</span>
            <strong className={data.auto_approved ? "text-emerald-700" : ""}>
              {data.auto_approved ? "Active" : "Under review"}
            </strong>
          </div>
          {data.partner_code && (
            <div className="flex justify-between mt-1"><span>Your partner code</span>
              <strong className="font-mono text-xs" data-testid="partner-paid-code">{data.partner_code}</strong>
            </div>
          )}
        </div>
        <Button asChild className="w-full mt-6 h-11 font-semibold" style={{ background: "var(--miro-navy)" }}>
          <Link to="/login" data-testid="partner-paid-login-btn">Sign in</Link>
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Small helpers / dumb components
// ============================================================================
function safeParse(s) { try { return s ? JSON.parse(s) : null; } catch { return null; } }

function SectionTitle({ title, subtitle }) {
  return (
    <div>
      <h2 className="font-display font-bold text-xl text-[var(--miro-navy)]">{title}</h2>
      {subtitle && <p className="text-sm text-[var(--miro-text-secondary)] mt-1">{subtitle}</p>}
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", testid, required, error }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={onChange}
             required={required}
             className={`mt-1 h-11 bg-white ${error ? "border-red-500 ring-1 ring-red-500" : ""}`}/>
      {error && (
        <p className="text-xs text-red-600 mt-1" data-testid={`${testid}-error`}>{error}</p>
      )}
    </div>
  );
}

function TrackCard({ title, description, feeLines, feeNote, selected, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border p-5 transition-colors ${
        selected
          ? "border-[var(--miro-blue)] bg-[var(--miro-blue)]/5 ring-2 ring-[var(--miro-blue)]/20"
          : "border-[var(--miro-border)] bg-white hover:border-[var(--miro-blue)]/50"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="font-semibold text-[var(--miro-navy)]">{title}</div>
        {selected && <Check size={16} className="text-[var(--miro-blue)] shrink-0"/>}
      </div>
      <div className="text-xs text-[var(--miro-text-secondary)] mt-1 leading-relaxed">
        {description}
      </div>
      {feeLines && (
        <div className="mt-3 space-y-1 border-t border-[var(--miro-border)] pt-3">
          {feeLines.map(([label, amount], i) => (
            <div key={label}
                 className={`flex justify-between text-xs ${
                   i === feeLines.length - 1
                     ? "font-bold text-[var(--miro-navy)] text-sm"
                     : "text-[var(--miro-text-secondary)]"
                 }`}>
              <span>{label}</span><span>{amount}</span>
            </div>
          ))}
        </div>
      )}
      {feeNote && (
        <div className="mt-3 border-t border-[var(--miro-border)] pt-3 text-[11px] text-[var(--miro-text-muted)] italic">
          {feeNote}
        </div>
      )}
    </button>
  );
}
